import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquare, Send, Loader2, Bot, User, FileText, Plus, ImagePlus, X, List, Search, StopCircle } from 'lucide-react';
import { chatbotService, ChatMessage, ChatListItem } from '../services/chatbotService';

interface ChatbotContentProps {
  compact?: boolean;
  onClose?: () => void;
  className?: string;
}

const ChatbotContent: React.FC<ChatbotContentProps> = ({ compact = false, onClose, className = '' }) => {
  const MAX_ATTACHMENTS = 10;
  const { t } = useTranslation();
  const [chats, setChats] = useState<ChatListItem[]>([]);
  const [currentChatId, setCurrentChatId] = useState<number | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingChats, setLoadingChats] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sources, setSources] = useState<{ folder: string; files: { name: string }[] } | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const chatsMenuRef = useRef<HTMLDivElement>(null);
  const [showChatsMenu, setShowChatsMenu] = useState(false);
  const skipNextChatLoadRef = useRef(false);
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});

  const loadChats = () => {
    setLoadingChats(true);
    chatbotService.getChats().then((res) => {
      if (res.success && res.chats) setChats(res.chats);
      setLoadingChats(false);
    }).catch(() => setLoadingChats(false));
  };

  useEffect(() => {
    loadChats();
    chatbotService.getSources().then((res) => {
      if (res.success) setSources({ folder: res.folder, files: res.files });
    }).catch(() => setSources(null));
  }, []);

  useEffect(() => {
    if (currentChatId == null) {
      setMessages([]);
      return;
    }
    if (skipNextChatLoadRef.current) {
      skipNextChatLoadRef.current = false;
      return;
    }
    chatbotService.getChat(currentChatId).then((res) => {
      if (res.success && res.messages) setMessages(res.messages);
    }).catch(() => setMessages([]));
  }, [currentChatId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!showChatsMenu) return;
    const close = (e: MouseEvent) => {
      if (chatsMenuRef.current && !chatsMenuRef.current.contains(e.target as Node)) setShowChatsMenu(false);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [showChatsMenu]);

  useEffect(() => {
    const nextUrls: Record<string, string> = {};
    const usedUrls = new Set<string>();
    files.forEach((file, idx) => {
      const key = `${file.name}-${file.size}-${idx}`;
      if (file.type.startsWith('image/')) {
        const existing = previewUrls[key];
        if (existing) {
          nextUrls[key] = existing;
          usedUrls.add(existing);
        } else {
          const created = URL.createObjectURL(file);
          nextUrls[key] = created;
          usedUrls.add(created);
        }
      }
    });

    Object.values(previewUrls).forEach((url) => {
      if (!usedUrls.has(url)) URL.revokeObjectURL(url);
    });

    setPreviewUrls(nextUrls);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  useEffect(() => {
    return () => {
      Object.values(previewUrls).forEach((url) => URL.revokeObjectURL(url));
    };
  }, [previewUrls]);

  const startNewChat = () => {
    setCurrentChatId(null);
    setMessages([]);
    setInput('');
    setFiles([]);
    setError(null);
    setShowChatsMenu(false);
    inputRef.current?.focus();
  };

  const selectChat = (id: number) => {
    setCurrentChatId(id);
    setShowChatsMenu(false);
  };

  const handleSearchWeb = async (messageIndex: number) => {
    setError(null);
    setLoading(true);
    try {
      // Get the full conversation history including all messages
      const history = messages.map((m) => ({ 
        role: m.role, 
        content: typeof m.content === 'string' ? m.content : '' 
      }));
      
      // Find the original question that triggered the search request
      // Look for the user message before the assistant message that has requiresChoice
      let originalQuestion = null;
      for (let i = messageIndex - 1; i >= 0; i--) {
        if (messages[i].role === 'user' && messages[i].content) {
          const content = typeof messages[i].content === 'string' ? messages[i].content : '';
          // Skip if it's already a search request
          if (!content.toLowerCase().includes('recherche') && !content.toLowerCase().includes('search')) {
            originalQuestion = content;
            break;
          }
        }
      }
      
      // Send the search request with full history
      const searchMessage = t('chatbot.searchWebShort', 'Recherche sur internet');
      const res = await chatbotService.sendMessage(
        searchMessage,
        history,
        currentChatId ?? undefined
      );
      
      if (res.success && res.reply && res.chat_id) {
        // Refresh chat to get updated messages
        chatbotService.getChat(res.chat_id).then((detail) => {
          if (detail.success && detail.messages && detail.messages.length > 0) {
            setMessages(detail.messages);
          } else {
            // Fallback: add messages manually
            setMessages((prev) => [
              ...prev, 
              { role: 'user', content: searchMessage }, 
              { role: 'assistant', content: res.reply }
            ]);
          }
        }).catch(() => {
          // Fallback on error
          setMessages((prev) => [
            ...prev, 
            { role: 'user', content: searchMessage }, 
            { role: 'assistant', content: res.reply! }
          ]);
        });
      } else {
        setError(t('chatbot.errorGeneric', 'Failed to get a response.'));
      }
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
        : (err instanceof Error ? err.message : 'Network error');
      setError(msg || t('chatbot.errorGeneric', 'Something went wrong.'));
    } finally {
      setLoading(false);
    }
  };

  const handleDismissChoice = (messageIndex: number) => {
    setMessages((prev) => prev.map((m, i) => (i === messageIndex ? { ...m, requiresChoice: false, choiceDismissed: true } : m)));
  };

  const appendAttachments = (incomingFiles: File[]) => {
    if (!incomingFiles.length) return;
    const allowed = incomingFiles.filter((file) => (
      file.type.startsWith('image/') || file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
    ));
    if (!allowed.length) return;
    setFiles((prev) => [...prev, ...allowed].slice(0, MAX_ATTACHMENTS));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = input.trim();
    if ((!trimmed && files.length === 0) || loading) return;
    setError(null);
    const userContent = trimmed || (files.length > 0 ? t('chatbot.imageSent', 'Image envoyée') : '');
    const userMsg: ChatMessage = { role: 'user', content: userContent };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    const filesToSend = files.slice(0, MAX_ATTACHMENTS);
    setFiles([]);
    setLoading(true);
    try {
      const history = messages.map((m) => ({ role: m.role, content: m.content }));
      const res = await chatbotService.sendMessage(trimmed || '(Attachment)', history, currentChatId ?? undefined, filesToSend);
      if (res.success && res.reply) {
        if (res.requiresChoice) {
          setMessages((prev) => [...prev, { role: 'assistant', content: res.reply, requiresChoice: true }]);
          if (res.chat_id != null) {
            skipNextChatLoadRef.current = true;
            setCurrentChatId(res.chat_id);
            if (!currentChatId) loadChats();
          }
        } else if (res.chat_id != null) {
          setCurrentChatId(res.chat_id);
          if (!currentChatId) loadChats();
          chatbotService.getChat(res.chat_id).then((detail) => {
            if (detail.success && detail.messages && detail.messages.length > 0) {
              setMessages(detail.messages);
            } else {
              setMessages((prev) => [...prev, { role: 'assistant', content: res.reply }]);
            }
          }).catch(() => {
            setMessages((prev) => [...prev, { role: 'assistant', content: res.reply }]);
          });
        } else {
          setMessages((prev) => [...prev, { role: 'assistant', content: res.reply }]);
        }
      } else {
        setError(t('chatbot.errorGeneric', 'Failed to get a response.'));
      }
    } catch (err: unknown) {
      let msg: string;
      if (err && typeof err === 'object' && 'response' in err) {
        const res = (err as { response?: { data?: { message?: string }; status?: number } }).response;
        msg = res?.data?.message || (res?.status === 408 ? t('chatbot.errorTimeout', 'Request took too long. Please try again.') : '');
      } else if (err && typeof err === 'object' && 'code' in err && (err as { code?: string }).code === 'ECONNABORTED') {
        msg = t('chatbot.errorTimeout', 'Request took too long. Please try again.');
      } else {
        msg = err instanceof Error ? err.message : '';
      }
      setError(msg || t('chatbot.errorNetwork', 'Network error. Check your connection and try again.'));
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const containerClass = compact
    ? 'h-full flex flex-col bg-white dark:bg-gray-900'
    : 'min-h-[calc(100vh-6rem)] flex flex-col bg-gradient-to-br from-slate-50 via-white to-teal-50/30 dark:from-gray-900 dark:via-gray-900 dark:to-teal-950/20';

  return (
    <div className={`${containerClass} ${className}`}>
      <div className="flex flex-1 min-h-0">
        {!compact && (
          <div className="w-56 flex-shrink-0 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/80 flex flex-col">
            <div className="p-2 border-b border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={startNewChat}
                className="w-full flex items-center justify-center gap-2 py-2 px-3 rounded-lg bg-teal-500 hover:bg-teal-600 text-white text-sm font-medium"
              >
                <Plus className="w-4 h-4" />
                {t('chatbot.newChat', 'Nouvelle conversation')}
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {loadingChats ? (
                <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-teal-500" /></div>
              ) : (
                <ul className="space-y-1">
                  {chats.map((c) => (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => setCurrentChatId(c.id)}
                        className={`w-full text-left py-2 px-3 rounded-lg text-sm truncate block ${
                          currentChatId === c.id
                            ? 'bg-teal-100 dark:bg-teal-900/40 text-teal-800 dark:text-teal-200 font-medium'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                        title={c.title || c.last_message || ''}
                      >
                        {c.title || c.last_message || t('chatbot.untitled', 'Sans titre')}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        <div className="flex-1 flex flex-col min-w-0">
          <div className="flex-shrink-0 border-b border-gray-200/80 dark:border-gray-700/80 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm">
            <div className={`flex items-center justify-between ${compact ? 'px-3 py-2' : 'max-w-4xl mx-auto px-4 py-4'}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className="p-2 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 shadow-lg shadow-teal-500/25 flex-shrink-0">
                  <MessageSquare className="w-5 h-5 text-white" />
                </div>
                <div className="min-w-0">
                  <h1 className={`font-bold text-gray-900 dark:text-white truncate ${compact ? 'text-base' : 'text-xl'}`}>
                    {t('chatbot.title', 'Assistant')}
                  </h1>
                  {!compact && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {t('chatbot.subtitle', 'Ask anything. Answers use your PDFs, then the web if needed.')}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {compact && (
                  <div className="relative" ref={chatsMenuRef}>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setShowChatsMenu((v) => !v); }}
                      className="p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      title={t('chatbot.chatsMenu', 'Chats')}
                      aria-expanded={showChatsMenu}
                    >
                      <List className="w-5 h-5" />
                    </button>
                    {showChatsMenu && (
                      <div className="absolute right-0 top-full mt-1 w-64 max-h-72 overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg z-50 py-1">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); startNewChat(); }}
                          className="w-full flex items-center gap-2 py-2 px-3 text-left text-sm font-medium text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/30"
                        >
                          <Plus className="w-4 h-4 flex-shrink-0" />
                          {t('chatbot.newChat', 'Nouvelle conversation')}
                        </button>
                        <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
                        {loadingChats ? (
                          <div className="flex justify-center py-3"><Loader2 className="w-5 h-5 animate-spin text-teal-500" /></div>
                        ) : (
                          <ul className="max-h-48 overflow-y-auto">
                            {chats.map((c) => (
                              <li key={c.id}>
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); selectChat(c.id); }}
                                  className={`w-full text-left py-2 px-3 text-sm truncate block ${
                                    currentChatId === c.id
                                      ? 'bg-teal-100 dark:bg-teal-900/40 text-teal-800 dark:text-teal-200 font-medium'
                                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                  }`}
                                  title={c.title || c.last_message || ''}
                                >
                                  {c.title || c.last_message || t('chatbot.untitled', 'Sans titre')}
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {sources && sources.files.length > 0 && !compact && (
                  <div className="hidden sm:flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400" title={sources.folder}>
                    <FileText className="w-4 h-4 text-teal-500" />
                    <span>{sources.files.length} PDF(s)</span>
                  </div>
                )}
                {onClose && (
                  <button type="button" onClick={onClose} className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" aria-label="Fermer">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto min-h-0">
            <div className={compact ? 'px-3 py-4 space-y-4' : 'max-w-4xl mx-auto px-4 py-6 space-y-6'}>
              {messages.length === 0 && (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <div className="p-3 rounded-2xl bg-teal-100/80 dark:bg-teal-900/30 mb-3">
                    <Bot className="w-10 h-10 text-teal-600 dark:text-teal-400" />
                  </div>
                  <h2 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-1">
                    {t('chatbot.welcomeTitle', 'How can I help?')}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xs">
                    {t('chatbot.welcomeText', "Ask a question. I'll use your company PDFs first, then search the web if needed.")}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">{t('chatbot.uploadImageHint', 'You can attach an image, drag & drop one here, or paste an image or any URL to analyze.')}</p>
                </div>
              )}
              {messages.map((msg, i) => (
                <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                  <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${
                    msg.role === 'user' ? 'bg-teal-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                  }`}>
                    {msg.role === 'user' ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                  </div>
                  <div className={`flex-1 max-w-[85%] ${msg.role === 'user' ? 'text-right' : ''}`}>
                    <div className={`inline-block px-3 py-2 rounded-xl text-left text-sm ${
                      msg.role === 'user'
                        ? 'bg-teal-500 text-white rounded-tr-md'
                        : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm rounded-tl-md'
                    }`}>
                      {(() => {
                        const urls = (Array.isArray(msg.content_image_urls) && msg.content_image_urls.length)
                          ? msg.content_image_urls
                          : (msg.content_image_url ? [msg.content_image_url] : []);
                        if (!urls.length) return null;
                        return (
                          <div className="mb-2 flex flex-col gap-2">
                            {urls.map((rawUrl, idx) => {
                              const url = String(rawUrl);
                              const isPdf = url.toLowerCase().endsWith('.pdf');
                              return isPdf ? (
                                <a
                                  key={`${url}-${idx}`}
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={`inline-flex items-center gap-2 px-2 py-1 rounded border text-xs hover:bg-gray-50 dark:hover:bg-gray-700 ${
                                    msg.role === 'user'
                                      ? 'border-white/60 text-white hover:bg-white/10 dark:hover:bg-white/10'
                                      : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
                                  }`}
                                >
                                  <FileText className="w-4 h-4" />
                                  <span className="truncate max-w-[220px]">{url.split('/').pop()}</span>
                                  <span
                                    className={`ml-1 text-[10px] px-1.5 py-0.5 rounded ${
                                      msg.role === 'user'
                                        ? 'bg-white/20 text-white'
                                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                                    }`}
                                  >
                                    PDF
                                  </span>
                                </a>
                              ) : (
                                <img
                                  key={`${url}-${idx}`}
                                  src={url}
                                  alt=""
                                  className="max-w-full max-h-48 rounded-lg object-contain"
                                />
                              );
                            })}
                          </div>
                        );
                      })()}
                      {msg.content ? (
                        <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                      ) : null}
                      {msg.role === 'assistant' && (() => {
                        const m = msg as ChatMessage & { requiresChoice?: boolean; choiceDismissed?: boolean };
                        if (m.choiceDismissed) return null;
                        const showChoiceButtons = m.requiresChoice === true ||
                          /souhaitez-vous que je recherche sur internet/i.test(msg.content || '');
                        return showChoiceButtons ? (
                          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600 flex flex-wrap gap-2 flex-shrink-0">
                            <button
                              type="button"
                              onClick={() => handleSearchWeb(i)}
                              disabled={loading}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-500 hover:bg-teal-600 disabled:opacity-50 text-white text-sm font-medium"
                            >
                              <Search className="w-4 h-4" />
                              {t('chatbot.searchWeb', 'Rechercher sur internet')}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDismissChoice(i)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium"
                            >
                              <StopCircle className="w-4 h-4" />
                              {t('chatbot.stopChoice', 'Arrêter')}
                            </button>
                          </div>
                        ) : null;
                      })()}
                    </div>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex gap-2">
                  <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                    <Bot className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                  </div>
                  <div className="px-3 py-2 rounded-xl rounded-tl-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm">
                    <Loader2 className="w-4 h-4 text-teal-500 animate-spin" />
                  </div>
                </div>
              )}
              {error && (
                <div className="rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-3 py-2 text-sm text-red-700 dark:text-red-300">{error}</div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          <div className="flex-shrink-0 border-t border-gray-200/80 dark:border-gray-700/80 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm">
            <div className={compact ? 'px-3 py-3' : 'max-w-4xl mx-auto px-4 py-4'}>
              {files.length > 0 && (
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  {files.slice(0, MAX_ATTACHMENTS).map((f, idx) => (
                    <div key={`${f.name}-${idx}`} className="flex items-center gap-2 px-2 py-1 rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-xs">
                      {f.type.startsWith('image/') ? (
                        <img
                          src={previewUrls[`${f.name}-${f.size}-${idx}`]}
                          alt={f.name}
                          className="w-8 h-8 rounded object-cover border border-gray-200 dark:border-gray-700"
                        />
                      ) : (
                        <FileText className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      )}
                      <span className="truncate max-w-[180px]">{f.name}</span>
                      <button
                        type="button"
                        onClick={() => {
                          setFiles(prev => prev.filter((_, i) => i !== idx));
                        }}
                        className="p-0.5 rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                        title="Retirer"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
                onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setDragOver(false);
                  const dropped = e.dataTransfer?.files;
                  if (dropped?.length) {
                    appendAttachments(Array.from(dropped));
                  }
                }}
                className={dragOver ? 'rounded-xl ring-2 ring-teal-400 ring-offset-2 dark:ring-offset-gray-900' : ''}
              >
              <form onSubmit={handleSubmit} className="flex gap-2 items-end">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,.pdf"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    const list = e.target.files ? Array.from(e.target.files) : [];
                    if (list.length) {
                      appendAttachments(list);
                    }
                    e.target.value = '';
                  }}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex-shrink-0 p-2.5 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  title={t('chatbot.attachImage', 'Attach image/PDF')}
                >
                  <ImagePlus className="w-5 h-5" />
                </button>
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onPaste={(e) => {
                    const items = Array.from(e.clipboardData?.items || []);
                    const pastedFiles = items
                      .filter((item) => item.kind === 'file')
                      .map((item) => item.getAsFile())
                      .filter((file): file is File => Boolean(file));
                    if (pastedFiles.length > 0) {
                      e.preventDefault();
                      appendAttachments(pastedFiles);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(e); }
                  }}
                  placeholder={t('chatbot.placeholder', 'Ask a question...')}
                  rows={1}
                  className="flex-1 min-h-[40px] max-h-28 px-3 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 resize-none text-sm"
                  disabled={loading}
                />
                <button
                  type="submit"
                  disabled={loading || (!input.trim() && files.length === 0)}
                  className="flex-shrink-0 p-2.5 rounded-xl bg-teal-500 hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed text-white shadow-lg shadow-teal-500/25 transition-colors"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                </button>
              </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatbotContent;
